import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const DriverProfile = () => {
  const [profile, setProfile] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    status: 'idle',
    current_vehicle: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchProfile();
    fetchAvailableVehicles();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/drivers/profile/');
      setProfile(response.data);
      setFormData({
        name: response.data.name || '',
        phone_number: response.data.phone_number || '',
        status: response.data.status || 'idle',
        current_vehicle: response.data.current_vehicle || response.data.current_vehicle_info?.id || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      // If profile doesn't exist, show form to create it
      if (error.response?.status === 404) {
        setMessage('Please complete your profile information below.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVehicles = async () => {
    try {
      const response = await api.get('/drivers/available_vehicles/');
      setAvailableVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching available vehicles:', error);
      // Fallback: try to get all vehicles
      try {
        const vehiclesRes = await api.get('/vehicles/');
        setAvailableVehicles(vehiclesRes.data || []);
      } catch (fallbackError) {
        console.error('Error fetching vehicles:', fallbackError);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // If status changes to 'not_working', clear vehicle selection
    if (name === 'status' && value === 'not_working') {
      setFormData(prev => ({
        ...prev,
        status: value,
        current_vehicle: '', // Clear vehicle when status is 'not_working'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Use PUT if profile exists, POST if it doesn't
      const method = profile ? 'put' : 'post';
      // Prepare data - convert empty string to null for vehicle
      const submitData = {
        name: formData.name,
        phone_number: formData.phone_number,
        status: formData.status,
        current_vehicle: formData.current_vehicle || null,
      };
      
      const response = await api[method]('/drivers/profile/', submitData);
      setMessage('Profile saved successfully');
      // Update local state with response data
      if (response.data) {
        setProfile(response.data);
        setFormData({
          name: response.data.name || '',
          phone_number: response.data.phone_number || '',
          status: response.data.status || 'idle',
          current_vehicle: response.data.current_vehicle || response.data.current_vehicle_info?.id || '',
        });
        // Refresh available vehicles in case assignments changed
        fetchAvailableVehicles();
      } else {
        // Refresh profile if no data in response
        fetchProfile();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      let errorMsg = 'Error saving profile. Please check your input.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (typeof error.response.data === 'object') {
          // Handle validation errors
          const errors = Object.values(error.response.data).flat();
          errorMsg = errors.join(', ') || errorMsg;
        }
      }
      
      setMessage(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container>Loading...</Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Driver Profile
        </Typography>
        <Paper sx={{ p: 3, mt: 3 }}>
          {message && (
            <Alert 
              severity={message.includes('success') ? 'success' : message.includes('Please') ? 'info' : 'error'} 
              sx={{ mb: 2 }}
            >
              {message}
            </Alert>
          )}
          {!profile && !loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Complete your profile to start using the service.
            </Alert>
          )}
          
          {profile && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>Current Profile Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{profile.email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{profile.name || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                  <Typography variant="body1">{profile.phone_number || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Typography variant="body1" textTransform="capitalize">
                    {profile.status || 'Not set'}
                  </Typography>
                </Grid>
                {profile.current_vehicle_info && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Current Vehicle</Typography>
                    <Typography variant="body1">
                      {profile.current_vehicle_info.display || 'Not assigned'}
                    </Typography>
                  </Grid>
                )}
                {profile.current_job_info && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Current Job</Typography>
                    <Typography variant="body1">
                      {profile.current_job_info.job_location} (
                      {profile.current_job_info.diesel_quantity > 0 && `${profile.current_job_info.diesel_quantity}L Diesel`}
                      {profile.current_job_info.diesel_quantity > 0 && profile.current_job_info.def_quantity > 0 && ' / '}
                      {profile.current_job_info.def_quantity > 0 && `${profile.current_job_info.def_quantity}L DEF`}
                      {(!profile.current_job_info.diesel_quantity || profile.current_job_info.diesel_quantity === 0) && (!profile.current_job_info.def_quantity || profile.current_job_info.def_quantity === 0) && '0L'}
                      )
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
            {profile ? 'Update Profile' : 'Create Profile'}
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="idle">Idle</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="refueling">Refueling</MenuItem>
                    <MenuItem value="not_working">Not Working</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Current Vehicle</InputLabel>
                  <Select
                    name="current_vehicle"
                    value={formData.current_vehicle}
                    onChange={handleChange}
                    label="Current Vehicle"
                    disabled={formData.status === 'not_working'}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {availableVehicles.map((vehicle) => (
                      <MenuItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                      </MenuItem>
                    ))}
                  </Select>
                  {formData.status === 'not_working' && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                      Vehicle will be unassigned when status is "Not Working"
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  size="large"
                >
                  {saving ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
};

export default DriverProfile;



